// BL598 Phase 2 — `gridctl get grids`.
//
// Lists every grid (org) the signed-in user belongs to, with role and member
// count, and marks the active grid. Reads GET /api/v2/grids, which bypasses the
// active-org middleware server-side so it works before any active grid is set.

import { Command } from 'commander';
import type { IV2Client } from '../../api/v2-client.js';
import { getV2Client } from '../../api/v2-client.js';
import { getActiveOrgSlug } from '../../active-org.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';

// Decision 045: dual-emit — prefer `grid_id`, fall back to legacy `org_id`.
interface OrgRow {
  grid_id?: string;
  org_id?: string;
  slug: string;
  name: string;
  role: string;
  member_count: number;
}

/** Resolve a row's grid id from the dual-emitted wire fields. */
function rowGridId(r: OrgRow): string {
  return r.grid_id ?? r.org_id ?? '';
}

export interface GetGridsOptions {
  json?: boolean;
  output?: string;
}

function renderTable(rows: OrgRow[], activeSlug: string | null, wide: boolean): string {
  if (rows.length === 0) {
    return 'You do not belong to any grids.';
  }
  // `-o wide` adds the grid id column (kubectl parity).
  const header = wide
    ? ['', 'SLUG', 'NAME', 'ROLE', 'MEMBERS', 'GRID_ID']
    : ['', 'SLUG', 'NAME', 'ROLE', 'MEMBERS'];
  const data = rows.map((r) => {
    const base = [
      r.slug === activeSlug ? '*' : '',
      r.slug,
      r.name,
      r.role,
      String(r.member_count),
    ];
    return wide ? [...base, rowGridId(r)] : base;
  });
  const widths = header.map((h, i) =>
    Math.max(h.length, ...data.map((row) => row[i].length)),
  );
  const fmt = (row: string[]) =>
    row.map((cell, i) => cell.padEnd(widths[i])).join('  ').trimEnd();
  const lines = [fmt(header), fmt(widths.map((w) => '-'.repeat(w)))];
  for (const row of data) lines.push(fmt(row));
  return lines.join('\n');
}

export async function runGetGrids(
  client: IV2Client,
  opts: GetGridsOptions,
  activeSlug: string | null,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const resp = await client.get<{
    orgs: OrgRow[];
    primary_grid_id?: string | null;
    primary_org_id?: string | null;
  }>('/api/v2/grids');
  const rows = resp.orgs ?? [];

  out.json(() => ({
    active_grid_slug: activeSlug,
    total: rows.length,
    grids: rows.map((r) => ({
      grid_id: rowGridId(r),
      slug: r.slug,
      name: r.name,
      role: r.role,
      member_count: r.member_count,
    })),
  }));
  out.human(() => renderTable(rows, activeSlug, out.isWide));
}

export const getGridsCommand = addOutputOption(
  new Command('grids')
    .description('List the grids you belong to')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (opts: GetGridsOptions) => {
  try {
    const client = await getV2Client();
    await runGetGrids(client, opts, getActiveOrgSlug());
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
